<div class="settings-box-shadow">
  <ng-template #disabledSitesPopContent>
    The same per-site controls as the extension popup: each row is a website, each switch is one
    FlyMSG feature on that site. Changes sync to the extension after you save.
  </ng-template>
  <h3 class="no-left-margin" style="margin-bottom: 0">
    Disabled
    <span class="font-weight-bold">Sites</span>
    <vng-icon
      name="info"
      class="info-icon"
      color="accent"
      [outline]="false"
      [ngbPopover]="disabledSitesPopContent"
      [closeDelay]="5000"
      triggers="mouseenter:mouseleave"
      placement="top"
    ></vng-icon>
  </h3>
  <p style="margin-top: 1em">
    Sites where you turned FlyMSG features off — from here or from the extension popup. A switch that
    is on means that feature works on the site; off means it stays quiet there. When every feature is
    off, the extension icon shows an OFF badge on that site.
  </p>

  <vng-card variant="ghost" padding="none" [formGroup]="form">
    <div class="disabled-sites-add">
      <vng-input
        class="disabled-sites-add-input"
        label="Website"
        placeholder="google.com"
        [formControl]="addSiteControl"
        [flyMsgIgnore]="true"
        (keyup.enter)="addSite()"
        [error]="
          addSiteControl.touched && addSiteControl.hasError('domain')
            ? 'Enter a domain like google.com'
            : ''
        "
      ></vng-input>
      <button
        vng-button
        variant="secondary"
        type="button"
        [disabled]="!addSiteControl.value?.trim()"
        (click)="addSite()"
      >
        Disable site
      </button>
    </div>

    <div class="disabled-sites-table-wrap" *ngIf="rows.length; else emptyState">
      <table class="disabled-sites-table">
        <thead>
          <tr>
            <th class="site-col">Website</th>
            <th>Shortcuts</th>
            <th>Grammar</th>
            <th>Paragraph Rewriter</th>
            <th *ngIf="sentenceRewriterAccessGranted">FlyMSG Sentence Rewriter</th>
            <th>
              Plugin
              <vng-icon
                name="info"
                class="plugin-info-icon"
                color="accent"
                [outline]="false"
                vngTooltip="Compose & toolbar buttons — only exists on Gmail, LinkedIn and Outlook"
                vngTooltipPosition="top"
              ></vng-icon>
            </th>
            <th>All features</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let row of rows; trackBy: trackByDomain">
            <td class="site-col">
              <span class="site-domain">{{ row.domain }}</span>
              <vng-badge
                *ngIf="row.fullyDisabled"
                variant="error"
                size="sm"
                class="site-state-badge"
                vngTooltip="Every FlyMSG feature you control is off on this site"
                vngTooltipPosition="top"
                >Off</vng-badge
              >
              <vng-badge
                *ngIf="row.partiallyDisabled"
                variant="warning"
                size="sm"
                class="site-state-badge"
                vngTooltip="Some FlyMSG features are off on this site, others still work"
                vngTooltipPosition="top"
                >Partial</vng-badge
              >
            </td>
            <td>
              <vng-toggle
                color="accent"
                [ngModel]="!row.shortcutsDisabled"
                [ngModelOptions]="{ standalone: true }"
                (changed)="onFeatureToggle(row, 'shortcuts', $event)"
                [ariaLabel]="'Shortcuts on ' + row.domain"
              ></vng-toggle>
            </td>
            <td>
              <span
                [vngTooltip]="
                  grammarControllable
                    ? ''
                    : grammarAdminOverride
                    ? 'This setting is controlled by your administrator'
                    : 'FlyGrammar is turned off for all sites — use the FlyGrammar card above'
                "
                vngTooltipPosition="top"
              >
                <vng-toggle
                  #grammarToggle
                  color="accent"
                  [ngModel]="!row.grammarDisabled"
                  [ngModelOptions]="{ standalone: true }"
                  [disabled]="!grammarControllable"
                  (changed)="onFeatureToggle(row, 'grammar', $event, grammarToggle)"
                  [ariaLabel]="'Grammar on ' + row.domain"
                ></vng-toggle>
              </span>
            </td>
            <td>
              <span
                [vngTooltip]="
                  rewriteControllable
                    ? ''
                    : rewriteAdminOverride
                    ? 'This setting is controlled by your administrator'
                    : 'The Paragraph Rewriter is turned off for all sites — use its card above'
                "
                vngTooltipPosition="top"
              >
                <vng-toggle
                  color="accent"
                  [ngModel]="!row.rewriteDisabled"
                  [ngModelOptions]="{ standalone: true }"
                  [disabled]="!rewriteControllable"
                  (changed)="onFeatureToggle(row, 'rewrite', $event)"
                  [ariaLabel]="'Paragraph Rewriter on ' + row.domain"
                ></vng-toggle>
              </span>
            </td>
            <td *ngIf="sentenceRewriterAccessGranted">
              <span
                [vngTooltip]="
                  sentenceRewriteControllable
                    ? ''
                    : sentenceRewriteAdminOverride
                    ? 'This setting is controlled by your administrator'
                    : 'The FlyMSG Sentence Rewriter is turned off for all sites — use its card above'
                "
                vngTooltipPosition="top"
              >
                <vng-toggle
                  color="accent"
                  [ngModel]="!row.sentenceRewriteDisabled"
                  [ngModelOptions]="{ standalone: true }"
                  [disabled]="!sentenceRewriteControllable"
                  (changed)="onFeatureToggle(row, 'sentence', $event)"
                  [ariaLabel]="'FlyMSG Sentence Rewriter on ' + row.domain"
                ></vng-toggle>
              </span>
            </td>
            <td>
              <vng-toggle
                *ngIf="row.pluginLabel !== null; else pluginNotApplicable"
                color="accent"
                [ngModel]="!row.pluginDisabled"
                [ngModelOptions]="{ standalone: true }"
                (changed)="onFeatureToggle(row, 'plugin', $event)"
                [ariaLabel]="'Plugin on ' + row.domain"
              ></vng-toggle>
              <ng-template #pluginNotApplicable>
                <span
                  class="plugin-na"
                  vngTooltip="The Plugin only exists on Gmail, LinkedIn and Outlook"
                  vngTooltipPosition="top"
                  >&mdash;</span
                >
              </ng-template>
            </td>
            <td>
              <vng-toggle
                color="accent"
                [ngModel]="!row.fullyDisabled"
                [ngModelOptions]="{ standalone: true }"
                (changed)="onSiteToggle(row, $event)"
                [ariaLabel]="'All FlyMSG features on ' + row.domain"
              ></vng-toggle>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <ng-template #emptyState>
      <p class="disabled-sites-empty">
        FlyMSG is on everywhere. Add a website above — or use the extension popup on any site — to
        turn features off there.
      </p>
    </ng-template>

    <div style="display:flex; justify-content:flex-end; align-items:center; gap:8px" class="settings-card-actions">
      <button
        vng-button
        variant="primary"
        type="button"
        [disabled]="!isDirty || isSaving"
        (click)="save()"
      >
        Save
      </button>
    </div>
  </vng-card>
</div>
